import React, { useCallback, useContext } from "react";
import TuneIcon from "@mui/icons-material/Tune";
import AddIcon from "@mui/icons-material/Add";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss";
import SearchField from "../inboxControls/searchField";
import GlobalTooltip from "../../../components/utility/tooltip/globalTooltip";
import { useShallow } from "zustand/react/shallow";
import { getDefaultInboxControlsState } from "../utils";
import { UserContext, UserContextPayload } from "../../../context/userContext";

interface ThreadListHeaderProps {
    onFilterClick: () => void;
}

/**
 * Thread list header with title actions and search.
 * Subscribes only to search-related store state so typing does not re-render the list.
 */
const ThreadListHeader: React.FC<ThreadListHeaderProps> = ({ onFilterClick }) => {

    const { userInfoSearchPhrase, setUserInfoSearchPhrase, searchQuery, setFilters, filters, setFlagNewThreadPopup } = useRuleBasedInboxStore(useShallow((state) => ({
        userInfoSearchPhrase: state.userInfoSearchPhrase,
        setUserInfoSearchPhrase: state.setUserInfoSearchPhrase,
        searchQuery: state.filters.searchQuery,
        setFilters: state.setFilters,
        filters: state.filters,
        setFlagNewThreadPopup: state.setFlagNewThreadPopup
    })))

    const { userDetail } = useContext(UserContext) as UserContextPayload;

    const onUserInfoSearch = useCallback((value: string[]) => {
        const searchString = value.join(",");
        const { filters } = useRuleBasedInboxStore.getState();
        if(searchString?.trim()?.length > 0) {
            setFilters({ 
                ...filters, 
                searchQuery: searchString,
                conversationType: "All"
            });
        } else {
            setFilters(getDefaultInboxControlsState(userDetail));
        }
    }, [filters, setFilters, userDetail]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setUserInfoSearchPhrase(event.target.value);
    }, [setUserInfoSearchPhrase]);

    // Handle Add New Thread Icon Click - open a popup
    const handleAddThreadClick = useCallback(() => {
        setFlagNewThreadPopup(true);
    }, [])

    return (
        <div className={`${styles.threadListHeader} ${userInfoSearchPhrase?.trim() ? styles.threadListHeaderPadding : ""}`}>
            <div className={styles.headerTopRow}>
                <span className={styles.headerTitle}>Conversations</span>
                <div className={styles.headerActionIconsGrid}>
                    <GlobalTooltip
                        title="Create New Conversation"
                        placement="top"
                        arrow
                    >
                        <div
                            className={styles.filterButton}
                            onClick={handleAddThreadClick}
                            role="button"
                            tabIndex={0}
                            aria-label="Add New Thread"
                        >
                            <AddIcon className={styles.filterIcon} />
                        </div>
                    </GlobalTooltip>
                    <GlobalTooltip
                        title="Inbox Controls"
                        placement="top"
                        arrow
                    >
                        <div
                            className={styles.filterButton}
                            onClick={onFilterClick}
                            role="button"
                            tabIndex={0}
                            aria-label="Open filters"
                        >
                            <TuneIcon className={styles.filterIcon} />
                        </div>
                    </GlobalTooltip>
                </div>
            </div>
            <div className={styles.searchBarContainer}>
                <SearchField
                    value={userInfoSearchPhrase}
                    searchQuery={searchQuery}
                    handleSearchChange={handleSearchChange}
                    resetSearchValue={() => setUserInfoSearchPhrase('')}
                    onSearch={onUserInfoSearch}
                />
            </div>
        </div>
    );
};

export default ThreadListHeader;
